import React, { lazy, Suspense } from "react";
import { ThemeProvider } from "@material-ui/styles";
import { CssBaseline } from "@material-ui/core";
import { BrowserRouter as Router, Route, Switch, Redirect } from "react-router-dom";
import { PersistGate } from 'redux-persist/lib/integration/react';

import eccoTheme from "./eccoTheme";

import { LoadingIndicatorProvider } from "./contexts/LoadingIndicator";
import SimpleRoute from "./Container/SimpleRoute";
import NestingRoute from "./Container/NestingRoute";
import Iframe from "./components/Iframe";

import store, { persistor } from "./redux/store";
import { Provider } from "react-redux";
import TagManager from 'react-gtm-module';
import { GOOGLE_TAGMANAGER } from "./main_config";
import { EMPRESAS_HOME, EMPRESAS_ENERGIA, EMPRESAS_CONSULTORIO, EMPRESAS_AUSENTISMO, EMPRESAS_CAPACITACION, EMPRESAS_SALVAMENTO, EMPRESAS_EVENTOS, EMPRESAS_TRASLADOS, EMPRESAS_LABORAL, EMPRESAS_CUIDADOS, EMPRESAS_AREA_PROTEGIDA, FAMILIAS_HOME, FAMILIAS_CUIDADOS, FAMILIAS_TRASLADOS, INFO_UTIL, LINEA_DENUNCIAS, DATOS_IMPOSITIVOS } from "./routes/paths";
import SuccessfulHiring from "./components/Hire/SuccessfulHiring";
import UnsuccessfulHiring from "./components/Hire/UnsuccessfulHiring"
import ProductInit from "./components/ProductInit"
import DevResetHire from "./components/DevResetHire";

const Home = lazy(() => import("./routes/home"));
const ProductsConsult = lazy(() => import("./routes/products/consult"));
const ProductsHire = lazy(() => import("./routes/products/hire"));
const ProductHired = lazy(() => import("./routes/products/hired"));
const Notes = lazy(() => import("./routes/notes"));
const Note = lazy(() => import("./routes/notes/note"));
const BranchesOffices = lazy(() => import("./routes/branchOffices"));
const NuestraEmpresa = lazy(() => import("./routes/nuestraEmpresa"));
const EmpresasHome = lazy(() => import("./routes/empresasHome"));
const EmpresasEnergiaPage = lazy(() => import("./routes/empresasEnergia"));
const EmpresasConsultorioPage = lazy(() => import("./routes/empresasConsultorio"));
const EmpresasAusentismoPage = lazy(() => import("./routes/empresasAusentismo"));
const EmpresasCapacitacionPage = lazy(() => import("./routes/empresasCapacitacion"));
const EmpresasSalvamentoPage = lazy(() => import("./routes/empresasSalvamento"));
const EmpresasEventosPage = lazy(() => import("./routes/empresasEventos"));
const EmpresasTrasladosPage = lazy(() => import("./routes/empresasTraslados"));
const EmpresasLaboralPage = lazy(() => import("./routes/empresasLaboral"));
const EmpresasCuidadosPage = lazy(() => import("./routes/empresasCuidados"));
const EmpresasAreaProtegidaPage = lazy(() => import("./routes/empresasAreaProtegida"));
const FamiliasHome = lazy(() => import("./routes/familiasHome"));
const FamiliasCuidadosPage = lazy(() => import("./routes/familiasCuidados"));
const FamiliasTrasladosPage = lazy(() => import("./routes/familiasTraslados"));
const InformacionUtil = lazy(() => import("./routes/informacionUtil"));
const LineaDenunciasPage = lazy(() => import("./routes/lineaDenuncias"));
const DatosImpositivosPage = lazy(() => import("./routes/datosImpositivos"));
const NotFound = lazy(() => import("./routes/notFound"));
const SolicitudBajaLanding = lazy(() => import("./routes/solicitudBaja"));
const Unsubscribe = lazy(() => import("./routes/Unsubscribe"));
const SolicitudArrepentimientoLanding = lazy(() => import("./routes/solicitudArrepentimiento"));
const Repentance = lazy(() => import("./routes/repentance"));
const MedicalLeave = lazy(() => import("./routes/gpc/medicalLeave"));
const GPCSuccess = lazy(() => import("./routes/gpc/success"));
const Asociate = lazy(() => import("./routes/asociate"));

const isLocalDev =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

if (GOOGLE_TAGMANAGER.ID && !isLocalDev) {
  TagManager.initialize({
    gtmId: GOOGLE_TAGMANAGER.ID,
    auth: GOOGLE_TAGMANAGER.AUTH,
    preview: GOOGLE_TAGMANAGER.PREVIEW_ENV
  });
}

const App = () => {
  return <Provider store={store}>
    <ThemeProvider theme={eccoTheme}>
      <LoadingIndicatorProvider>
        <CssBaseline />
        <PersistGate loading={null} persistor={persistor}>
          <Router basename={"/individuales"}>
            {isLocalDev && <DevResetHire />}
            <Suspense fallback={"."}>
              <Switch>
                <SimpleRoute exact path="/" component={Home} isHome={true} />
                <Route
                  path="/productos"
                  render={() => <Redirect to={{
                    pathname: "/st/productos"
                  }} />}
                  exact
                /> 
                <SimpleRoute
                  path="/productos/contratar/inicio"
                  component={ProductInit}
                  isProduct={true}
                  LayoutProps={{
                    isProduct: true
                  }}
                />
                <SimpleRoute
                  path="/productos/contratar/:modulo"
                  component={ProductsHire}
                  isProduct={true}
                  LayoutProps={{
                    isProduct: true
                  }}
                />
                <SimpleRoute
                  path="/productos/contratado"
                  component={ProductHired}
                  isProduct={true}
                  LayoutProps={{
                    isProduct: true
                  }}
                />
                <SimpleRoute
                  path="/productos/finalizado"
                  component={SuccessfulHiring}
                  isProduct={true}
                  LayoutProps={{
                    isProduct: true
                  }}
                />
                <SimpleRoute
                  path="/productos/pendiente"
                  component={UnsuccessfulHiring}
                  isProduct={true}
                  LayoutProps={{
                    isProduct: true
                  }}
                />
                <SimpleRoute
                  path="/productos/consulta/:prod?/:servicio?"
                  component={ProductsConsult}
                  isProduct={true}
                  LayoutProps={{
                    isProduct: true
                  }}
                />
                <SimpleRoute
                  path="/gpc"
                  component={MedicalLeave}
                  LayoutProps={{
                    noHeader: true,
                    noFooter: true,
                    showFloatsButtons: false
                  }}
                />
                <SimpleRoute
                  path="/e/gpc/success"
                  component={GPCSuccess}
                  LayoutProps={{
                    noHeader: true,
                    noFooter: true,
                    showFloatsButtons: false
                  }}
                />
                <SimpleRoute
                  path="/e/gpc/controlausentismo"
                  component={MedicalLeave}
                  LayoutProps={{
                    noHeader: true,
                    noFooter: true,
                    showFloatsButtons: false
                  }}
                />
                <NestingRoute
                  path="/st/productos/:prod"
                  component={() => <Iframe file="productos" title="Productos"/>}
                  scrollTop={false}
                  nesting={[
                    {
                      path: "/consulta",
                      component: ProductsConsult,
                      exact: false
                    }
                  ]}
                />
                <SimpleRoute
                  exact
                  path="/st/:file"
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                  component={props => (
                    <Iframe file={props.computedMatch.params.file} />
                  )}
                />
                <NestingRoute
                  path="/notas"
                  component={Notes}
                  isNotes={true}
                  nesting={[
                    {
                      path: "/:slug",
                      component: Note,
                      exact: true,
                      isHome: false,
                      isNotes: false
                    }
                  ]}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path="/sucursales"
                  component={BranchesOffices}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path="/nuestra-empresa"
                  component={NuestraEmpresa}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={EMPRESAS_HOME}
                  component={EmpresasHome}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={EMPRESAS_ENERGIA}
                  component={EmpresasEnergiaPage}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={EMPRESAS_CONSULTORIO}
                  component={EmpresasConsultorioPage}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={EMPRESAS_AUSENTISMO}
                  component={EmpresasAusentismoPage}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={EMPRESAS_CAPACITACION}
                  component={EmpresasCapacitacionPage}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={EMPRESAS_SALVAMENTO}
                  component={EmpresasSalvamentoPage}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={EMPRESAS_EVENTOS}
                  component={EmpresasEventosPage}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={EMPRESAS_TRASLADOS}
                  component={EmpresasTrasladosPage}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={EMPRESAS_LABORAL}
                  component={EmpresasLaboralPage}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={EMPRESAS_CUIDADOS}
                  component={EmpresasCuidadosPage}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={EMPRESAS_AREA_PROTEGIDA}
                  component={EmpresasAreaProtegidaPage}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={FAMILIAS_HOME}
                  component={FamiliasHome}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={FAMILIAS_CUIDADOS}
                  component={FamiliasCuidadosPage}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={FAMILIAS_TRASLADOS}
                  component={FamiliasTrasladosPage}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={INFO_UTIL}
                  component={InformacionUtil}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={LINEA_DENUNCIAS}
                  component={LineaDenunciasPage}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path={DATOS_IMPOSITIVOS}
                  component={DatosImpositivosPage}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  path={["/login", "/recuperar-cuenta", "/registracion", "/panel"]}
                  component={NotFound}
                  LayoutProps={{
                    showFloatsButtons: false
                  }}
                />
                <SimpleRoute
                  exact
                  path="/solicitud-baja"
                  component={SolicitudBajaLanding}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path="/solicitud-baja/formulario"
                  component={Unsubscribe}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path="/solicitud-arrepentimiento"
                  component={SolicitudArrepentimientoLanding}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <SimpleRoute
                  exact
                  path="/solicitud-arrepentimiento/formulario"
                  component={Repentance}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <Route
                  path="/asociate"
                  component={Asociate}
                  LayoutProps={{
                    showFloatsButtons: true
                  }}
                />
                <Route path="*" render={() => <Redirect to={{
                  pathname: "/"
                }} />} />
              </Switch>
            </Suspense>
          </Router>
        </PersistGate>
      </LoadingIndicatorProvider>
    </ThemeProvider>
  </Provider>
};

export default App;
